import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { environment } from '../../../environments/environment';

export type S3UploadFolder = 'profileImage' | 'familyLogo' | 'memberPhoto' | 'storymedia';

@Injectable({ providedIn: 'root' })
export class S3ImageUploadService {
  private platformId = inject(PLATFORM_ID);

  private folderFor(type: S3UploadFolder): string {
    switch (type) {
      case 'profileImage': return environment.S3_BUCKET_FOLDER_PROFILE_IMAGE;
      case 'familyLogo':   return environment.S3_BUCKET_FOLDER_FAMILY_LOGO;
      case 'memberPhoto':  return environment.S3_BUCKET_FOLDER_MEMBER_PHOTO;
      case 'storymedia':   return environment.S3_BUCKET_FOLDER_STORY_MEDIA;
    }
  }

  /**
   * Upload a file to the configured S3 bucket using a pre-signed PUT URL.
   * The backend should provide the pre-signed URL; this method performs the
   * actual binary upload and returns the public object URL.
   *
   * Usage pattern:
   *  1. Call your API to get a pre-signed URL for the desired key.
   *  2. Pass it to uploadViaPresignedUrl() along with the File.
   *  3. Store the returned public URL in your form/state.
   */
  async uploadViaPresignedUrl(presignedUrl: string, file: File): Promise<string> {
    const resp = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!resp.ok) {
      throw new Error(`S3 upload failed: ${resp.status} ${resp.statusText}`);
    }

    // Strip query string to return the clean public URL
    return presignedUrl.split('?')[0];
  }

  /**
   * Build the public S3 object URL for a given key (file name).
   * Useful when the API returns just a filename and you need the full URL
   * to display images.
   */
  buildPublicUrl(folder: S3UploadFolder, fileName: string): string {
    const region = environment.S3_REGION;
    const bucket = environment.S3_BUCKET_NAME;
    const key = `${this.folderFor(folder)}/${fileName}`;
    return `https://${bucket}.s3.${region}.amazonaws.com/${key}`;
  }

  /**
   * Check whether S3 is configured in this environment.
   */
  isConfigured(): boolean {
    return !!(environment.S3_BUCKET_NAME && environment.S3_REGION);
  }

  /**
   * Upload a story media file (video/audio) from a blob URL to the S3
   * `storymedia` folder. Returns only the generated filename (not the full URL)
   * so it can be passed directly to the create-story API as `mediaFileName`.
   */
  async uploadStoryMedia(blobUrl: string, mediaType: string): Promise<string> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('S3 upload is only supported in the browser.');
    }

    const resp = await fetch(blobUrl);
    const blob = await resp.blob();

    const mimeToExt: Record<string, string> = {
      'video/webm': 'webm',
      'video/mp4':  'mp4',
      'audio/webm': 'webm',
      'audio/mpeg': 'mp3',
      'audio/ogg':  'ogg',
      'image/jpeg': 'jpg',
      'image/png':  'png',
    };

    const lowerType = mediaType.toLowerCase();
    const ext = mimeToExt[blob.type]
      ?? (lowerType === 'audio' ? 'webm' : lowerType === 'image+audio' ? 'jpg' : 'webm');

    const fileName = `${crypto.randomUUID()}.${ext}`;
    const key      = `${environment.S3_BUCKET_FOLDER_STORY_MEDIA}/${fileName}`;

    const client = new S3Client({
      region: environment.S3_REGION,
      credentials: {
        accessKeyId:     environment.S3_ACCESS_KEY_ID,
        secretAccessKey: environment.S3_SECRET_ACCESS_KEY,
      },
    });

    const arrayBuffer = await blob.arrayBuffer();
    await client.send(new PutObjectCommand({
      Bucket:      environment.S3_BUCKET_NAME,
      Key:         key,
      Body:        new Uint8Array(arrayBuffer),
      ContentType: blob.type || 'application/octet-stream',
    }));

    return fileName;
  }
}

